import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import classNames from "classnames";
import Modal from "./Modal";
import { useAuth } from "../../hooks/useAuth";
import { canSignOrgContracts } from "../../utils/receivedContracts";
import { firstSignPath } from "../../utils/requiredDocsLock";

// The required-documents prompt, in two modes:
//   reminder — days 1-7, dismissible once per session (Layout queues it).
//   locked   — past the deadline. Dismissible: the lock gates signing, not the
//              modules, so RequiredDocsLockHost shows it on login and whenever
//              a sign or order attempt is refused, and the user can close it
//              and keep working.
//
// Composed exactly like ModalUpgradeRequired and reuses its styles via the
// shared `upgrade-modal` class so the two stay visually in sync; only the
// document-list rows and the pill are new (`.required-docs-modal` in
// _modal.scss). The hover-to-un-blur interaction is intentionally dropped —
// there is nothing to peek at here.
export default function ModalRequiredDocsPrompt({ mode = "reminder", lock, close }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth, hasGroup } = useAuth();

  const locked = mode === "locked";
  const canSign = canSignOrgContracts(auth, hasGroup);
  const items = lock?.items || [];

  const formatDate = (iso) => {
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? "" : new Date(ms).toLocaleDateString();
  };

  const itemTitle = (item) => {
    if (item.titleKey) return t(item.titleKey);
    return item.title || t("common.requiredDocsLock.docs.contract");
  };

  // Status is spelled out in words, not only by icon colour, so the row is
  // readable without colour vision.
  const itemStatus = (item) => {
    if (item.signedExternally) return t("common.requiredDocsLock.status.signedExternally");
    if (item.signed) return t("common.requiredDocsLock.status.signed");
    if (item.overdue) return t("common.requiredDocsLock.status.overdueSince", { date: formatDate(item.dueAt) });
    if (item.dueAt) return t("common.requiredDocsLock.status.due", { date: formatDate(item.dueAt) });
    return t("common.requiredDocsLock.status.outstanding");
  };

  // Bare "/settings" is not a declared route; /settings/profile is one every
  // signed-in user can open.
  const goSign = () => {
    navigate(firstSignPath(lock) || "/settings/profile");
    close?.();
  };

  return (
    <Modal
      show={true}
      close={close}
      size="small"
      className="upgrade-modal required-docs-modal"
      action={
        <>
          <button className="gray" onClick={close}>
            {locked ? t("common.requiredDocsLock.buttons.close") : t("common.requiredDocsLock.buttons.maybeLater")}
          </button>
          {canSign && (
            <button className="blue" onClick={goSign}>
              {t("common.requiredDocsLock.buttons.signNow")}
              <i className="material-icons ml-5">chevron_right</i>
            </button>
          )}
        </>
      }
    >
      <div className="upgrade-required-wrapper">
        <div className="upgrade-preview-section">
          <div className="upgrade-preview-content">
            <div className="preview-card">
              <div className="preview-header">
                <div className="preview-icon-wrapper">
                  <i className="material-icons">description</i>
                </div>
                <div className="preview-text">
                  <div className="preview-line preview-line-title"></div>
                  <div className="preview-line preview-line-subtitle"></div>
                </div>
              </div>
              <div className="preview-items">
                <div className="preview-item">
                  <i className="material-icons preview-check">check_circle</i>
                  <div className="preview-line preview-line-item"></div>
                </div>
                <div className="preview-item">
                  <i className="material-icons preview-clock">schedule</i>
                  <div className="preview-line preview-line-item-short"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="required-docs-pill-layer">
            <div className="required-docs-pill">
              <i className="material-icons">{locked ? "lock" : "edit_calendar"}</i>
              <span>{locked ? t("common.requiredDocsLock.pill.locked") : t("common.requiredDocsLock.pill.reminder")}</span>
            </div>
          </div>
        </div>
        <div className="upgrade-content">
          <div className="upgrade-badge">
            <i className="material-icons">{locked ? "error_outline" : "schedule"}</i>
            <span>
              {locked
                ? t("common.requiredDocsLock.badge.actionRequired")
                : t("common.requiredDocsLock.badge.daysLeft", { count: lock?.daysLeft ?? 0 })}
            </span>
          </div>
          <h2 className="upgrade-title">{t("common.requiredDocsLock.title")}</h2>
          <p className="upgrade-description">
            {canSign
              ? locked
                ? t("common.requiredDocsLock.description.locked")
                : t("common.requiredDocsLock.description.reminder")
              : t("common.requiredDocsLock.description.nonSigner")}
          </p>
          {items.length > 0 && (
            <ul className="required-docs-list">
              {items.map((item) => (
                <li key={item.key} className={classNames("required-docs-row", { signed: item.signed, overdue: item.overdue })}>
                  <i className="material-icons">{item.signed ? "check_circle" : "schedule"}</i>
                  <span className="required-docs-row-title">{itemTitle(item)}</span>
                  <span className="required-docs-row-status">{itemStatus(item)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
